/**
 * Frontend mirror of /app/backend/vital_specs.py.
 * Single source of truth for the 35 kiosk metrics.
 *
 * Kept in sync manually with the backend. When the backend adds/changes a metric,
 * update the corresponding entry here too. In production we could ship /api/vital-specs
 * on app boot and cache it, but a compile-time constant keeps type-safety cheap.
 */

export const CATEGORY_ORDER = [
    "cardio",
    "weight",
    "fat",
    "muscle",
    "fluid",
    "bone",
    "sensory",
];

export const CATEGORY_LABELS = {
    cardio: "Cardiovascular & Vitals",
    weight: "Weight & BMI",
    fat: "Fat Composition",
    muscle: "Muscle & Metabolic",
    fluid: "Body Fluids & Hydration",
    bone: "Bone & Structural",
    sensory: "Sensory Profile",
};

export const CATEGORY_ICONS = {
    cardio: "Heart",
    weight: "Scale",
    fat: "Zap",
    muscle: "Dumbbell",
    fluid: "Droplet",
    bone: "Bone",
    sensory: "Eye",
};

export const VITAL_SPECS = [
    // Cardio
    { key: "blood_pressure_systolic", label: "Systolic Pressure", unit: "mmHg", category: "cardio", min: 90, max: 140, higherIsBad: true, lowerIsBad: true },
    { key: "blood_pressure_diastolic", label: "Diastolic Pressure", unit: "mmHg", category: "cardio", min: 60, max: 90, higherIsBad: true, lowerIsBad: true },
    { key: "heart_rate", label: "Heart Rate (Resting)", unit: "bpm", category: "cardio", min: 60, max: 100, higherIsBad: true, lowerIsBad: true },
    { key: "max_heart_rate", label: "Max Heart Rate (Est.)", unit: "bpm", category: "cardio", min: null, max: null },
    { key: "spo2", label: "Blood Oxygen (SpO₂)", unit: "%", category: "cardio", min: 95, max: 100, lowerIsBad: true },
    { key: "body_temperature", label: "Body Temperature", unit: "°C", category: "cardio", min: 36.1, max: 37.2, higherIsBad: true, lowerIsBad: true },

    // Weight
    { key: "height_cm", label: "Height", unit: "cm", category: "weight" },
    { key: "weight_kg", label: "Weight", unit: "kg", category: "weight", min: 60.9, max: 82.0, higherIsBad: true, lowerIsBad: true },
    { key: "bmi", label: "BMI", unit: "kg/m²", category: "weight", min: 18.5, max: 24.9, higherIsBad: true, lowerIsBad: true },
    { key: "target_weight_change_kg", label: "Target Weight Change", unit: "kg", category: "weight" },
    { key: "ideal_weight_kg", label: "Ideal Weight", unit: "kg", category: "weight" },
    { key: "body_surface_area_m2", label: "Body Surface Area", unit: "m²", category: "weight" },
    { key: "overweight_percent", label: "Overweight", unit: "%", category: "weight", min: null, max: 0, higherIsBad: true },

    // Fat
    { key: "fat_mass_kg", label: "Fat Mass", unit: "kg", category: "fat", min: 6.1, max: 17.2, higherIsBad: true, lowerIsBad: true },
    { key: "body_fat_percent", label: "Body Fat", unit: "%", category: "fat", min: 10.0, max: 21.0, higherIsBad: true, lowerIsBad: true },
    { key: "visceral_fat_level", label: "Visceral Fat Level", unit: "", category: "fat", min: 1, max: 9, higherIsBad: true },
    { key: "subcutaneous_fat_kg", label: "Subcutaneous Fat", unit: "kg", category: "fat", min: 4.9, max: 12.3, higherIsBad: true, lowerIsBad: true },
    { key: "subcutaneous_fat_percent", label: "Subcutaneous Fat", unit: "%", category: "fat", min: 8.0, max: 15.0, higherIsBad: true, lowerIsBad: true },

    // Muscle & Metabolic
    { key: "muscle_mass_kg", label: "Muscle Mass", unit: "kg", category: "muscle", min: 40.2, max: 69.7, lowerIsBad: true },
    { key: "muscle_percent", label: "Muscle", unit: "%", category: "muscle", min: 50.0, max: 65.0, lowerIsBad: true },
    { key: "skeletal_muscle_kg", label: "Skeletal Muscle", unit: "kg", category: "muscle", min: 34.1, max: 59.1, lowerIsBad: true },
    { key: "skeletal_muscle_percent", label: "Skeletal Muscle", unit: "%", category: "muscle", min: 56.0, max: 72.0, lowerIsBad: true },
    { key: "protein_kg", label: "Protein Mass", unit: "kg", category: "muscle", min: 9.8, max: 16.4, lowerIsBad: true },
    { key: "protein_percent", label: "Protein", unit: "%", category: "muscle", min: 16.0, max: 20.0, higherIsBad: true, lowerIsBad: true },
    { key: "basal_metabolism_kcal", label: "Basal Metabolism", unit: "kcal", category: "muscle", min: 1395, max: 1705, lowerIsBad: true },
    { key: "lean_body_mass_kg", label: "Lean Body Mass", unit: "kg", category: "muscle", min: 44.0, max: 65.1, lowerIsBad: true },

    // Fluid
    { key: "body_water_volume_kg", label: "Body Water Volume", unit: "kg", category: "fluid", min: 30.5, max: 53.3, lowerIsBad: true },
    { key: "body_water_percent", label: "Body Water", unit: "%", category: "fluid", min: 50.0, max: 65.0, higherIsBad: true, lowerIsBad: true },
    { key: "intracellular_fluid_kg", label: "Intracellular Fluid (ICF)", unit: "kg", category: "fluid", min: 20.1, max: 35.3, lowerIsBad: true },
    { key: "intracellular_fluid_rate", label: "ICF Rate", unit: "%", category: "fluid", min: 33.0, max: 43.0, higherIsBad: true, lowerIsBad: true },
    { key: "extracellular_fluid_kg", label: "Extracellular Fluid (ECF)", unit: "kg", category: "fluid", min: 10.4, max: 18.0, higherIsBad: true, lowerIsBad: true },
    { key: "extracellular_fluid_rate", label: "ECF Rate", unit: "%", category: "fluid", min: 17.0, max: 22.0, higherIsBad: true, lowerIsBad: true },
    { key: "raw_impedance_ohm", label: "Raw Impedance", unit: "Ω", category: "fluid" },

    // Bone
    { key: "bone_mass_kg", label: "Bone Mass", unit: "kg", category: "bone", min: 3.2, max: null, lowerIsBad: true },
    { key: "bone_salt_kg", label: "Bone Salt", unit: "kg", category: "bone", min: 2.7, max: 3.7, lowerIsBad: true },

    // Sensory
    { key: "left_eye_vision", label: "Left Eye Vision", unit: "", category: "sensory", min: 0.8, max: null, lowerIsBad: true },
    { key: "right_eye_vision", label: "Right Eye Vision", unit: "", category: "sensory", min: 0.8, max: null, lowerIsBad: true },
    { key: "color_blindness", label: "Color Blindness", unit: "", category: "sensory" },
];

export const VITAL_BY_KEY = Object.fromEntries(VITAL_SPECS.map((v) => [v.key, v]));

/** Return one of "normal", "high", "low", "unknown" for a metric value. */
export function statusOf(key, value) {
    if (value === null || value === undefined || value === "") return "unknown";
    const spec = VITAL_BY_KEY[key];
    if (!spec) return "unknown";
    const num = typeof value === "number" ? value : parseFloat(value);
    if (Number.isNaN(num)) return "unknown";
    if (spec.max !== null && spec.max !== undefined && num > spec.max && spec.higherIsBad) return "high";
    if (spec.min !== null && spec.min !== undefined && num < spec.min && spec.lowerIsBad) return "low";
    return "normal";
}

export function formatRange(spec) {
    if (spec.min === null || spec.min === undefined) {
        if (spec.max === null || spec.max === undefined) return "—";
        return `≤ ${spec.max}`;
    }
    if (spec.max === null || spec.max === undefined) return `≥ ${spec.min}`;
    return `${spec.min}–${spec.max}`;
}
